import React, { useEffect, useState, useMemo } from "react";
import { DynamicVariableInput } from "./DynamicVariableInput";
import { analyzeWorkflowContext, buildContextAwareCategories } from "./workflowVariableUtils";
import { type Node, type Edge } from "@xyflow/react";
import { useGetPublishedFormListQuery, useLazyGetFormByIdQuery } from "@/services/FormBuilderApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { useGetTriggerableWorkflowsQuery } from "@/services/WorkFlowBuilderApi";
import { useGetStaffListQuery } from "@/services/StaffsApi";
import { useGetAllTasksQuery } from "@/services/TasksApi";
import { useGetAllListingOfTicketsQuery } from "@/services/LiveQueueService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/Select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/Popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/Command";
import { Task, TaskStatus } from "@/app/types/tasks";
import { TicketStatus } from "@/services/LiveQueueService";
import { Button } from "@/components/ui/Button";
import { Check, ChevronsUpDown, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const DEFAULT_SYSTEM_PROMPT = `give only "confidence score" and "content" variables in the output json. no explanations, only json.`;

interface PropertiesPanelProps {
  selectedNode: Node | null;
  selectedEdge: Edge | null;
  nodes: Node[];
  edges: Edge[];
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  onDelete: (nodeId: string) => void;
  onDeleteEdge: (edgeId: string) => void;
  workflowName: string;
  setWorkflowName: (name: string) => void;
  showNameError: boolean;
  setShowNameError: (show: boolean) => void;
  onCreateWorkflow: () => void;
  onRunWorkflow: () => void;
  isCreating: boolean;
}

interface Rule {
  field: string;
  operator: string;
  value: string;
  source?: string;
}

type SimpleConditionConfig = Rule;

interface ComplexConditionConfig {
  logic: "AND" | "OR";
  conditions: Rule[];
}

type ConditionConfig = SimpleConditionConfig | ComplexConditionConfig;

interface ScheduleConfig {
  interval: number;
  schedule_type: string;
  start_date_time: string;
  end_date_time: string;
  timezone: string;
}

interface EventConfig {
  source_id: number | null;
  task_id?: number | null;
  ticket_id?: number | null;
  current_status?: string | null;
  new_status?: string | null;
}

interface ActionConfig {
  action_type: string;
  action_config: {
    to?: string;
    email_subject?: string;
    email_body?: string;
    message?: string;
    number?: string;
    // Webhook fields
    url?: string;
    method?: string;
    headers?: Record<string, string>;
    body?: string;
    // Read form responses fields
    form_id?: number | string | null;
    conditions?: string;
    status?: string;
    workflow_id?: string | number;

    assign_to?: string;
    title?: string;
  };
}

interface TriggerConfig {
  trigger_type?: "schedule" | "event";
  event_trigger_type?: string;
  schedule_config?: ScheduleConfig;
  event_config?: EventConfig;
}

const ACTION_LABELS: Record<string, string> = {
  email: "Send Email",
  whatsapp: "Send WhatsApp",
  sms: "Send SMS",
  webhook: "API Request",
  read_form_responses: "Read Form Responses",
  update_form_status: "Update Form Status",
  ticket_creation: "Create Ticket",
  task_creation: "Create Task",
  sub_workflow: "Sub workflow",
};

export const PropertiesPanel = ({
  selectedNode,
  selectedEdge,
  nodes,
  edges,
  setNodes,
  onDelete,
  onDeleteEdge,
  workflowName,
  setWorkflowName,
  showNameError,
  setShowNameError,
}: PropertiesPanelProps) => {
  const [label, setLabel] = useState("");
  const [actionType, setActionType] = useState("email");
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [conditionLogic, setConditionLogic] = useState<"AND" | "OR">("AND");
  const [conditionRules, setConditionRules] = useState<Rule[]>([
    { field: "", operator: "equals", value: "", source: "trigger" },
  ]);
  const [triggerType, setTriggerType] = useState<"schedule" | "event">("schedule");
  const [scheduleConfig, setScheduleConfig] = useState<ScheduleConfig>({
    interval: 1,
    schedule_type: "day",
    start_date_time: "",
    end_date_time: "",
    timezone: "PKT",
  });
  const [eventConfig, setEventConfig] = useState<EventConfig>({
    source_id: null,
    task_id: null,
    ticket_id: null,
    current_status: null,
    new_status: null,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookMethod, setWebhookMethod] = useState("GET");
  const [webhookHeaders, setWebhookHeaders] = useState("");
  const [webhookBody, setWebhookBody] = useState("");
  const [formId, setFormId] = useState<string | number | null>(null);
  const [formConditions, setFormConditions] = useState("");
  const [formStatus, setFormStatus] = useState("archived");
  const [showDeleteNodeDialog, setShowDeleteNodeDialog] = useState(false);
  const [openFormPopover, setOpenFormPopover] = useState(false);
  const [openWorkflowPopover, setOpenWorkflowPopover] = useState(false);
  const [showDeleteEdgeDialog, setShowDeleteEdgeDialog] = useState(false);
  const [formFields, setFormFields] = useState<{ id: string; label: string }[]>([]);
  const [subWorkflowId, setSubWorkflowId] = useState<string | number | null>(null);
  const [openTaskPopover, setOpenTaskPopover] = useState(false);
  const [openTicketPopover, setOpenTicketPopover] = useState(false);
  const [taskSearch, setTaskSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");
  const [taskOffset, setTaskOffset] = useState(0);
  const [ticketOffset, setTicketOffset] = useState(0);
  const [accumulatedTasks, setAccumulatedTasks] = useState<any[]>([]);
  const [accumulatedTickets, setAccumulatedTickets] = useState<any[]>([]);

  const [title, setTitle] = useState("");
  const loadedFormIdRef = React.useRef<number | null>(null);

  // special states for staffList
  const { data: staffList } = useGetStaffListQuery({ query: "" });
  const [assignedTo, setAssignedTo] = useState("");
  const [openAssigneePopover, setOpenAssigneePopover] = useState(false);
  const [showSystemPrompt, setShowSystemPrompt] = useState(false);

  // Human Node Configuration State - Single state object
  const [humanConfig, setHumanConfig] = useState<{
    assignee_email: string;
    assign_to: string;
    instructions: string;
    timeout_minutes: string;
  }>({
    assignee_email: "",
    assign_to: "",
    instructions: "",
    timeout_minutes: "60",
  });

  // AI Node Configuration State - Single state object
  const [aiConfig, setAiConfig] = useState<{
    prompt: string;
    system_prompt: string;
    timeout_seconds: string;
    confidence_threshold: string;
  }>({
    prompt: "",
    system_prompt: "",
    timeout_seconds: "60",
    confidence_threshold: "",
  });

  const { data: formList, isLoading: isFormListLoading } = useGetPublishedFormListQuery();
  const [getFormById, { data: formData }] = useLazyGetFormByIdQuery();

  const { data: workflowList, isLoading: isWorkflowListLoading } = useGetTriggerableWorkflowsQuery();

  const currentTaskLimit = taskOffset === 0 ? 50 : 20;
  const { data: tasksTable, isFetching: isTasksFetching } = useGetAllTasksQuery({
    query: `limit=${currentTaskLimit}&offset=${taskOffset}${taskSearch ? `&search=${taskSearch}` : ""}&sort_key=id&sort_order=desc`
  });

  const currentTicketLimit = ticketOffset === 0 ? 50 : 20;
  const { data: ticketsTable, isFetching: isTicketsFetching } = useGetAllListingOfTicketsQuery({
    query: `limit=${currentTicketLimit}&offset=${ticketOffset}${ticketSearch ? `&search=${ticketSearch}` : ""}&sort_key=id&sort_order=desc`
  });

  const taskList = useMemo(() => accumulatedTasks.map(t => ({ value: t.task_id, label: t.title })), [accumulatedTasks]);
  const ticketListOptions = useMemo(() => accumulatedTickets.map(t => ({ value: t.id, label: `${t.ticket_number} - ${t.title}` })), [accumulatedTickets]);

  const handleTaskScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
      if (!isTasksFetching && tasksTable && accumulatedTasks.length < tasksTable.filtered_count) {
        console.log('Task scroll bottom reached, increasing offset from:', taskOffset);
        setTaskOffset(prev => prev + (prev === 0 ? 50 : 20));
      }
    }
  };

  const handleTicketScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
      if (!isTicketsFetching && ticketsTable && accumulatedTickets.length < ticketsTable.filtered_count) {
        console.log('Ticket scroll bottom reached, increasing offset from:', ticketOffset);
        setTicketOffset(prev => prev + (prev === 0 ? 50 : 20));
      }
    }
  };

  // Consolidated Task Data Accumulation & Reset
  useEffect(() => {
    if (!selectedNode?.id) {
      setAccumulatedTasks([]);
      setTaskOffset(0);
      setTaskSearch("");
      return;
    }

    if (tasksTable?.data) {
      console.log('PropertiesPanel - Tasks Table Data Arrived:', tasksTable.data.length, 'Offset:', taskOffset, 'Node:', selectedNode.id);
      if (taskOffset === 0) {
        // Fresh start for this node/search
        setAccumulatedTasks(tasksTable.data);
      } else {
        setAccumulatedTasks(prev => {
          const existingIds = new Set(prev.map(t => t.task_id));
          const newItems = tasksTable.data.filter(t => !existingIds.has(t.task_id));
          if (newItems.length === 0) return prev;
          console.log('PropertiesPanel - Appending new tasks:', newItems.length);
          return [...prev, ...newItems];
        });
      }
    }
  }, [tasksTable?.data, taskOffset, selectedNode?.id]);

  // Consolidated Ticket Data Accumulation & Reset
  useEffect(() => {
    if (!selectedNode?.id) {
      setAccumulatedTickets([]);
      setTicketOffset(0);
      setTicketSearch("");
      return;
    }

    if (ticketsTable?.data) {
      console.log('PropertiesPanel - Tickets Table Data Arrived:', ticketsTable.data.length, 'Offset:', ticketOffset, 'Node:', selectedNode.id);
      if (ticketOffset === 0) {
        // Fresh start for this node/search
        setAccumulatedTickets(ticketsTable.data);
      } else {
        setAccumulatedTickets(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const newItems = ticketsTable.data.filter(t => !existingIds.has(t.id));
          if (newItems.length === 0) return prev;
          console.log('PropertiesPanel - Appending new tickets:', newItems.length);
          return [...prev, ...newItems];
        });
      }
    }
  }, [ticketsTable?.data, ticketOffset, selectedNode?.id]);

  const taskStatusOptions = useMemo(() => Object.values(TaskStatus).map(s => ({ value: s, label: s.replace(/_/g, ' ').toUpperCase() })), []);
  const ticketStatusOptions = useMemo(() => Object.values(TicketStatus).map(s => ({ value: s, label: s.replace(/_/g, ' ').toUpperCase() })), []);

  useEffect(() => {
    loadedFormIdRef.current = null;
  }, [selectedNode?.id]);

  useEffect(() => {
    setTaskOffset(0);
    // Removed setAccumulatedTasks([]) to avoid race condition with cached data
  }, [taskSearch]);

  useEffect(() => {
    setTicketOffset(0);
    // Removed setAccumulatedTickets([]) to avoid race condition with cached data
  }, [ticketSearch]);

  useEffect(() => {
    setTaskOffset(0);
  }, [openTaskPopover]);

  useEffect(() => {
    setTicketOffset(0);
  }, [openTicketPopover]);

  // Compute context-aware variable categories based on workflow structure
  const contextAwareCategories = useMemo(() => {
    const context = analyzeWorkflowContext(selectedNode, nodes, edges);
    const applicableActionType = selectedNode?.type === "action" ? actionType : undefined;
    return buildContextAwareCategories(context, formFields, applicableActionType, selectedNode?.type);
  }, [selectedNode, nodes, edges, formFields, actionType]);

  useEffect(() => {
    if (selectedNode) {
      setLabel((selectedNode.data.label as string) || "");

      // Load action config if it exists
      if (selectedNode.type === "action" && selectedNode.data.config) {
        const config = selectedNode.data.config as ActionConfig;
        setActionType(config.action_type || "email");

        const to = config.action_config?.to || "";
        setEmailTo(to);

        setEmailSubject(config.action_config?.email_subject || "");
        setEmailBody(config.action_config?.email_body || "");

        const phone = config.action_config?.number || "";
        setWhatsappPhone(phone);

        const msg = config.action_config?.message || "";
        setWhatsappMessage(msg);

        if (config.action_type === "webhook") {
          setWebhookUrl(config.action_config?.url || "");
          setWebhookMethod(config.action_config?.method || "GET");
          setWebhookHeaders(config.action_config?.headers ? JSON.stringify(config.action_config.headers, null, 2) : "");
          setWebhookBody(config.action_config?.body || "");
        }

        if (config.action_type === "read_form_responses") {
          const hasContext = hasPreviousFormContext(selectedNode, nodes, edges);
          const defaultFid = hasContext ? "{source_id}" : "";
          const fid = config.action_config?.form_id || defaultFid;

          setFormId(fid);
          setFormConditions(config.action_config?.conditions || "");

          if (!config.action_config?.form_id) {
            setNodes((nds) => nds.map((n) => {
              if (n.id === selectedNode.id) {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    config: {
                      ...config,
                      action_config: { ...config.action_config, form_id: fid }
                    }
                  }
                };
              }
              return n;
            }));
          }
        }

        if (config.action_type === "update_form_status") {
          const hasContext = hasPreviousFormContext(selectedNode, nodes, edges);
          const defaultFid = hasContext ? "{source_id}" : "";
          const fid = config.action_config?.form_id || defaultFid;

          setFormId(fid);
          setFormStatus(config.action_config?.status || "archived");

          if (!config.action_config?.form_id) {
            setNodes((nds) => nds.map((n) => {
              if (n.id === selectedNode.id) {
                return {
                  ...n,
                  data: {
                    ...n.data,
                    config: {
                      ...config,
                      action_config: { ...config.action_config, form_id: fid }
                    }
                  }
                };
              }
              return n;
            }));
          }
        }

        if (config.action_type === "sub_workflow") {
          setSubWorkflowId(config.action_config?.workflow_id || null);
        }

        if (config.action_type === "ticket_creation" || config.action_type === "task_creation") {
          setAssignedTo(config.action_config?.assign_to || "");
          setTitle(config.action_config?.title || "");
        }
      } else if (selectedNode.type === "condition" && selectedNode.data.config) {
        // Load condition config
        const config = selectedNode.data.config as ConditionConfig;

        // Helper to check if it's a complex config
        const isComplex = (c: any): c is ComplexConditionConfig => "logic" in c && "conditions" in c;
        // Helper to check if it's a simple config (has field)
        const isSimple = (c: any): c is SimpleConditionConfig => "field" in c;

        if (isComplex(config)) {
          setConditionLogic(config.logic || "AND");
          setConditionRules(config.conditions || []);
        } else if (isSimple(config)) {
          setConditionLogic("AND"); // Default for display, though single rule ignores logic
          setConditionRules([config]);
        } else {
          // Fallback/Legacy or empty
          const legacyConfig = config as any;
          const rules = legacyConfig?.branch_config?.conditions?.rules || [];
          setConditionLogic(legacyConfig?.branch_config?.conditions?.logic || "AND");
          if (rules.length > 0) {
            setConditionRules(rules);
          } else {
            setConditionRules([]);
          }
        }
      } else if (selectedNode.type === "human" && selectedNode.data.config) {
        // Load Human config
        const config = selectedNode.data.config as any;
        const loadedHumanConfig = config.human_config || {};

        setHumanConfig({
          instructions: loadedHumanConfig.instructions || "",
          timeout_minutes: loadedHumanConfig.timeout_minutes?.toString() || "60",
          assignee_email: loadedHumanConfig.assignee_email || "",
          assign_to: loadedHumanConfig.assign_to || "",
        });
        setAssignedTo(loadedHumanConfig.assign_to || "");
      } else if (selectedNode.type === "ai" && selectedNode.data.config) {
        // Load AI config
        const config = selectedNode.data.config as any;
        const loadedAiConfig = config.ai_config || {};

        setAiConfig({
          prompt: loadedAiConfig.prompt || "",
          system_prompt: loadedAiConfig.system_prompt || DEFAULT_SYSTEM_PROMPT,
          timeout_seconds: loadedAiConfig.timeout_seconds?.toString() || loadedAiConfig.timeout?.toString() || "60",
          confidence_threshold: loadedAiConfig.confidence_threshold?.toString() || loadedAiConfig.result_evaluation?.confidence_threshold?.toString() || "",
        });
      } else if (selectedNode.type === "trigger" && selectedNode.data.config) {
        // Load trigger config
        const config = selectedNode.data.config as TriggerConfig;

        // Determine trigger type from config or label
        let type: "schedule" | "event" = "schedule";
        if (config.trigger_type) {
          type = config.trigger_type;
        } else if (label.toLowerCase().includes("form")) {
          type = "event";
        }

        setTriggerType(type);
        if (config.schedule_config) setScheduleConfig(config.schedule_config);
        if (config.event_config) setEventConfig(config.event_config);
      } else {
        // Reset action config fields if not an action node or condition node
        setActionType("email");

        setEmailTo("");
        setEmailSubject("");
        setEmailBody("");
        setWhatsappPhone("");
        setWhatsappMessage("");
        setConditionLogic("AND");
        setConditionRules([]);
        setTriggerType("schedule");
        setScheduleConfig({
          interval: 1,
          schedule_type: "day",
          start_date_time: "",
          end_date_time: "",
          timezone: "PKT",
        });
        setEventConfig({
          source_id: null,
          task_id: null,
          ticket_id: null,
          current_status: null,
          new_status: null,
        });
        setWebhookUrl("");
        setWebhookMethod("GET");
        setWebhookHeaders("");
        setWebhookBody("");
        setFormId(null);
        setFormConditions("");
        setFormStatus("archived");
        setSubWorkflowId(null);
        setAssignedTo("");
        // Reset Human config
        setHumanConfig({
          instructions: "",
          timeout_minutes: "60",
          assignee_email: "",
          assign_to: "",
        });
        // Reset AI config
        setAiConfig({
          prompt: "",
          system_prompt: DEFAULT_SYSTEM_PROMPT,
          timeout_seconds: "60",
          confidence_threshold: "",
        });
      }
    }
  }, [selectedNode]);

  // Helper function to determine field prefix based on source node
  const getFieldPrefixAndFormId = (node: Node, currentNodes: Node[], currentEdges: Edge[]): { prefix: string; formId: number | null; sourceType?: string } => {
    const visited = new Set<string>();
    const queue = [node.id];

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      // Find edges that connect TO the current node
      const incomingEdges = currentEdges.filter(edge => edge.target === currentId);

      for (const edge of incomingEdges) {
        const sourceNode = currentNodes.find(n => n.id === edge.source);
        if (!sourceNode) continue;

        // Check if this source node provides form context
        if (sourceNode.type === "trigger") {
          const triggerConfig = sourceNode.data.config as TriggerConfig;
          if (triggerConfig?.trigger_type === "event" &&
            triggerConfig.event_trigger_type === "form_submission" &&
            triggerConfig.event_config?.source_id) {
            return {
              prefix: "form_response",
              formId: triggerConfig.event_config.source_id,
              sourceType: "trigger"
            };
          }

          if (triggerConfig?.trigger_type === "event" &&
            triggerConfig.event_trigger_type === "waha_message") {
            return {
              prefix: "waha_message",
              formId: null,
              sourceType: "waha_trigger"
            };
          }
        } else if (sourceNode.type === "action") {
          const actionConfig = sourceNode.data.config as ActionConfig;
          if (actionConfig?.action_type === "read_form_responses") {
            // Get form_id from the read_form_responses action config
            const actionFormId = actionConfig.action_config?.form_id;
            let parsedFormId: number | null = null;
            if (actionFormId && actionFormId !== "{source_id}") {
              parsedFormId = typeof actionFormId === 'string' ? parseInt(actionFormId, 10) : actionFormId;
              if (isNaN(parsedFormId as number)) parsedFormId = null;
            }
            return {
              prefix: "action_result.form_responses.data",
              formId: parsedFormId,
              sourceType: "read_form_responses"
            };
          }
        }

        // Add to queue to continue searching upwards
        queue.push(sourceNode.id);
      }
    }

    return { prefix: "", formId: null };
  };

  // Helper function to check for form context in previous nodes (backtracking)
  const hasPreviousFormContext = (node: Node, currentNodes: Node[], currentEdges: Edge[]): boolean => {
    const visited = new Set<string>();
    const stack = [node.id];

    while (stack.length > 0) {
      const currentId = stack.pop()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      // Find edges that connect TO this node
      const incomingEdges = currentEdges.filter(edge => edge.target === currentId);

      for (const edge of incomingEdges) {
        const sourceNode = currentNodes.find(n => n.id === edge.source);
        if (!sourceNode) continue;

        // Check if this source node provides form context
        if (sourceNode.type === "trigger") {
          const config = sourceNode.data.config as TriggerConfig;
          if (config?.trigger_type === "event" && config.event_trigger_type === "form_submission") {
            return true;
          }
        } else if (sourceNode.type === "action") {
          const config = sourceNode.data.config as ActionConfig;
          // If it's a read action AND it already has a form context (from its own config or previous)
          if (config?.action_type === "read_form_responses") {
            // We consider it a source of context for subsequent nodes
            return true;
          }
        }

        stack.push(sourceNode.id);
      }
    }

    return false;
  };

  // Helper function to check for WhatsApp context in previous nodes
  const hasPreviousWhatsAppContext = (node: Node, currentNodes: Node[], currentEdges: Edge[]): boolean => {
    const visited = new Set<string>();
    const stack = [node.id];

    while (stack.length > 0) {
      const currentId = stack.pop()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);

      const incomingEdges = currentEdges.filter(edge => edge.target === currentId);

      for (const edge of incomingEdges) {
        const sourceNode = currentNodes.find(n => n.id === edge.source);
        if (!sourceNode) continue;

        if (sourceNode.type === "trigger") {
          const config = sourceNode.data.config as TriggerConfig;
          if (config?.trigger_type === "event" && config.event_trigger_type === "waha_message") {
            return true;
          }
        }
        stack.push(sourceNode.id);
      }
    }
    return false;
  };

  // Fetch form fields when any node is selected and has a source with form data
  useEffect(() => {
    if (!selectedNode) return;

    let formIdToFetch: number | null = null;

    if (selectedNode.type === "trigger") {
      const config = selectedNode.data.config as TriggerConfig;
      if (config?.trigger_type === "event" && config?.event_trigger_type === "form_submission") {
        formIdToFetch = config.event_config?.source_id || null;
      }
    } else {
      const { formId } = getFieldPrefixAndFormId(selectedNode, nodes, edges);
      formIdToFetch = formId;
    }

    if (formIdToFetch && formIdToFetch !== 0) {
      if (formIdToFetch !== loadedFormIdRef.current) {
        loadedFormIdRef.current = formIdToFetch;
        getFormById(formIdToFetch);
      }
      // If formId matches loadedFormIdRef.current, we do nothing (keep current formFields)
    } else {
      // Clear form fields if no valid source
      loadedFormIdRef.current = null;
      setFormFields([]);
    }
  }, [selectedNode, nodes, edges, getFormById]);

  useEffect(() => {
    if (!selectedNode) return;

    let currentPrefix = "";
    let currentSourceType = "";

    if (selectedNode.type === "trigger") {
      const config = selectedNode.data.config as TriggerConfig;
      if (config?.trigger_type === "event" && config.event_trigger_type === "form_submission") {
        currentPrefix = "form_response";
        currentSourceType = "trigger";
      } else if (config?.trigger_type === "event" && config.event_trigger_type === "waha_message") {
        currentPrefix = "waha_message";
        currentSourceType = "waha_trigger";
      }
    } else {
      const { prefix, sourceType } = getFieldPrefixAndFormId(selectedNode, nodes, edges);
      currentPrefix = prefix;
      currentSourceType = sourceType || "";
    }

    if (currentSourceType === "read_form_responses") {
      // Show response count variable and form fields with action_result prefix
      const baseFields = [{ id: "action_result.response_count", label: "Response Count" }];
      if (formData?.fields) {
        const dynamicFields = formData.fields.map((field: any) => ({
          id: `action_result.form_responses.data.${field.field_id}`,
          label: field.label
        }));
        setFormFields([...baseFields, ...dynamicFields]);
      } else {
        setFormFields(baseFields);
      }
    } else if (currentSourceType === "waha_trigger") {
      setFormFields([
        { id: "{payload.from}", label: "Message From" }
      ]);
    } else if (formData?.fields && currentSourceType === "trigger") {
      const fields = formData.fields.map((field: any) => ({
        id: currentPrefix ? `${currentPrefix}.${field.field_id}` : field.field_id,
        label: field.label
      }));
      setFormFields(fields);
    } else {
      setFormFields([]);
    }
  }, [formData, selectedNode, nodes, edges]);

  // Automatically select the first form if no form is selected and no previous context
  useEffect(() => {
    if (selectedNode?.type === "action" && formList && formList.length > 0) {
      if (actionType === "read_form_responses" || actionType === "update_form_status") {
        if (!hasPreviousFormContext(selectedNode, nodes, edges)) {
          if (!formId || formId === "{source_id}") {
            const firstFormId = formList[0].value.toString();
            setFormId(firstFormId);
            handleActionConfigChange("form_id", firstFormId);
          }
        }
      }
    } else if (selectedNode?.type === "trigger" && triggerType === "event" && formList && formList.length > 0) {
      const triggerConfig = selectedNode.data.config as TriggerConfig;
      if (triggerConfig?.event_trigger_type === "form_submission") {
        if (!eventConfig.source_id) {
          const firstFormId = parseInt(formList[0].value.toString());
          // Update local state
          setEventConfig(prev => ({ ...prev, source_id: firstFormId }));
          // Update node data
          handleTriggerConfigChange("event", "source_id", firstFormId.toString());
        }
      }
    }
  }, [selectedNode, formList, actionType, triggerType, nodes, edges, eventConfig.source_id]);

  // Automatically select the first workflow for sub_workflow action
  useEffect(() => {
    if (selectedNode?.type === "action" && actionType === "sub_workflow" && workflowList && workflowList.length > 0) {
      // Only auto-select if no workflow is currently selected
      if (!subWorkflowId && subWorkflowId !== 0) {
        const firstWorkflowId = workflowList[0].id;
        setSubWorkflowId(firstWorkflowId);
        handleActionConfigChange("workflow_id", firstWorkflowId);
      }
    }
  }, [selectedNode, actionType, workflowList, subWorkflowId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLabel(e.target.value);
    if (selectedNode) {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === selectedNode.id) {
            return {
              ...node,
              data: {
                ...node.data,
                label: e.target.value,
              },
            };
          }
          return node;
        })
      );
    }
  };

  const handleActionConfigChange = (field: string, value: string) => {
    if (!selectedNode || selectedNode.type !== "action") return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const currentConfig = (node.data.config as ActionConfig) || {
            action_type: "email",
            action_config: { to: "", email_subject: "", email_body: "" },
          };

          let updatedConfig: ActionConfig;
          let newWebhookUrl = webhookUrl;
          let newWebhookMethod = webhookMethod;
          let newWebhookHeaders = webhookHeaders;
          let newWebhookBody = webhookBody;

          if (field === "action_type") {
            // Reset fields when type changes if needed, but keeping state is fine
            // If switching TO webhook, ensure state is fresh or default
            let newActionConfig = value === "webhook" ? { url: newWebhookUrl, method: newWebhookMethod, headers: {}, body: newWebhookBody } : currentConfig.action_config || {};

            if (value === "read_form_responses" || value === "update_form_status") {
              const hasContext = hasPreviousFormContext(node, nds, edges);
              const defaultFid = hasContext ? "{source_id}" : "";
              newActionConfig = { ...newActionConfig, form_id: defaultFid };
              setFormId(defaultFid);
            }

            updatedConfig = {
              ...currentConfig,
              action_type: value,
              action_config: newActionConfig
            };
          } else if (field === "webhook_url") {
            newWebhookUrl = value;
            setWebhookUrl(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, url: value }
            }
          } else if (field === "webhook_method") {
            newWebhookMethod = value;
            setWebhookMethod(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, method: value }
            }
          } else if (field === "webhook_headers") {
            newWebhookHeaders = value;
            setWebhookHeaders(value);
            let headers = {};
            try {
              headers = value ? JSON.parse(value) : {};
            } catch (e) { /* ignore */ }

            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, headers: headers }
            }
          } else if (field === "webhook_body") {
            newWebhookBody = value;
            setWebhookBody(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, body: value }
            }
          } else if (field === "form_id") {
            const formIdValue = value;
            setFormId(formIdValue);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, form_id: formIdValue }
            }
          } else if (field === "form_conditions") {
            setFormConditions(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, conditions: value }
            }
          } else if (field === "form_status") {
            setFormStatus(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, status: value }
            }
          } else if (field === "workflow_id") {
            setSubWorkflowId(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, workflow_id: value }
            }
          } else if (field === "assign_to") {
            setAssignedTo(value);
            updatedConfig = {
              ...currentConfig,
              action_config: { ...currentConfig.action_config, assign_to: value }
            }
          } else {
            updatedConfig = {
              ...currentConfig,
              action_config: {
                ...currentConfig.action_config,
                [field]: value,
              },
            };
          }

          // If current action type is webhook (and not changing type), ensure structure
          if (updatedConfig.action_type === "webhook") {
            let headers = {};
            try {
              headers = newWebhookHeaders ? JSON.parse(newWebhookHeaders) : {};
            } catch (e) { }

            updatedConfig.action_config = {
              url: newWebhookUrl,
              method: newWebhookMethod,
              headers: headers,
              body: newWebhookBody
            };
          }

          // Ensure form_id is {source_id} for specific actions
          if ((updatedConfig.action_type === "read_form_responses" || updatedConfig.action_type === "update_form_status") && !updatedConfig.action_config?.form_id) {
            updatedConfig.action_config = {
              ...updatedConfig.action_config,
              form_id: "{source_id}"
            };
          }

          return {
            ...node,
            data: {
              ...node.data,
              config: updatedConfig,
            },
          };
        }
        return node;
      })
    );
  };

  const getNewConfig = (logic: "AND" | "OR", rules: Rule[]): ConditionConfig => {
    if (rules.length === 1) {
      // Return SimpleConditionConfig
      return {
        field: rules[0].field,
        operator: rules[0].operator,
        value: rules[0].value,
        source: rules[0].source
      };
    } else {
      // Return ComplexConditionConfig
      return {
        logic: logic,
        conditions: rules
      };
    }
  };

  const handleConditionConfigChange = (
    type: "logic" | "rule",
    value: any,
    ruleIndex?: number,
    ruleField?: keyof Rule
  ) => {
    if (!selectedNode || selectedNode.type !== "condition") return;

    let newLogic = conditionLogic;
    let newRules = [...conditionRules];

    if (type === "logic") {
      newLogic = value;
      setConditionLogic(value);
    } else if (type === "rule" && ruleIndex !== undefined && ruleField) {
      newRules[ruleIndex] = { ...newRules[ruleIndex], [ruleField]: value };
      setConditionRules(newRules);
    }

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              config: getNewConfig(newLogic, newRules),
            },
          };
        }
        return node;
      })
    );
  };

  const handleHumanConfigChange = (field: keyof typeof humanConfig, value: any) => {
    if (!selectedNode || selectedNode.type !== "human") return;

    // Update local state
    setHumanConfig((prev) => ({ ...prev, [field]: value }));

    // Update node config
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const currentConfig = node.data.config || {} as any;
          const currentHumanConfig = currentConfig.human_config || {};

          let updatedValue = value;
          if (field === "timeout_minutes") {
            updatedValue = parseInt(value || "60");
          }

          const updatedHumanConfig = {
            ...currentHumanConfig,
            [field]: updatedValue,
          };

          // Remove optional fields if empty/falsy to maintain clean config
          if (!updatedHumanConfig.instructions) delete updatedHumanConfig.instructions;
          if (!updatedHumanConfig.assignee_email) delete updatedHumanConfig.assignee_email;
          if (!updatedHumanConfig.assign_to) delete updatedHumanConfig.assign_to;

          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...currentConfig,
                human_config: updatedHumanConfig,
              },
            },
          };
        }
        return node;
      })
    );
  };

  const handleAIConfigChange = (field: keyof typeof aiConfig, value: any) => {
    if (!selectedNode || selectedNode.type !== "ai") return;

    // Update local state
    setAiConfig((prev) => ({ ...prev, [field]: value }));

    // Update node config
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const currentConfig = node.data.config || {} as any;
          const currentAiConfig = currentConfig.ai_config || {};

          let updatedValue = value;
          if (field === "timeout_seconds") {
            updatedValue = parseInt(value || "60");
          } else if (field === "confidence_threshold") {
            updatedValue = parseFloat(value);
          }

          const updatedAiConfig = {
            ...currentAiConfig,
            [field]: updatedValue,
          };

          // Special handling for confidence_threshold to match nested structure if needed
          // The previous code put it in result_evaluation
          if (field === "confidence_threshold" && value) {
            updatedAiConfig.result_evaluation = {
              ...(updatedAiConfig.result_evaluation || {}),
              confidence_threshold: parseFloat(value),
            };
          }

          return {
            ...node,
            data: {
              ...node.data,
              config: {
                ...currentConfig,
                ai_config: updatedAiConfig,
              },
            },
          };
        }
        return node;
      })
    );
  };

  const handleTriggerConfigChange = (
    type: "type" | "schedule" | "event" | "webhook",
    field: string,
    value: any
  ) => {
    if (!selectedNode || selectedNode.type !== "trigger") return;

    let newTriggerType = triggerType;
    let newScheduleConfig = { ...scheduleConfig };
    let newEventConfig = { ...eventConfig };

    if (type === "type") {
      newTriggerType = value;
      setTriggerType(value);
    } else if (type === "schedule") {
      if (field === "interval") value = parseInt(value) || 0;
      newScheduleConfig = { ...newScheduleConfig, [field]: value };
      setScheduleConfig(newScheduleConfig);
    } else if (type === "event") {
      if (field === "source_id") {
        const intVal = parseInt(value, 10);
        value = !isNaN(intVal) ? intVal : null;
      }
      if (field === "task_id" || field === "ticket_id") {
        const intVal = parseInt(value, 10);
        value = !isNaN(intVal) ? intVal : null;
      }
      newEventConfig = { ...newEventConfig, [field]: value };

      // UX Prefill: if we just selected a task/ticket, prefill current_status with its actual status
      if (field === "task_id" && value) {
        const selectedTask = accumulatedTasks.find(t => t.task_id.toString() === value.toString());
        if (selectedTask) {
          newEventConfig.current_status = selectedTask.status;
        }
      } else if (field === "ticket_id" && value) {
        const selectedTicket = accumulatedTickets.find(t => t.id.toString() === value.toString());
        if (selectedTicket) {
          newEventConfig.current_status = selectedTicket.status;
        }
      }

      setEventConfig(newEventConfig);
    }

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === selectedNode.id) {
          const config: TriggerConfig = {};

          if (newTriggerType === "schedule") {
            config.trigger_type = "schedule";
            config.schedule_config = newScheduleConfig;
          } else if (newTriggerType === "event") {
            config.trigger_type = "event";
            config.event_trigger_type = (selectedNode.data.config as TriggerConfig).event_trigger_type || "form_submission";
            config.event_config = newEventConfig;
          }

          return {
            ...node,
            data: {
              ...node.data,
              config: config,
            },
          };
        }
        return node;
      })
    );
  };

  const addRule = () => {
    const newRule: Rule = { field: "", operator: "equals", value: "", source: "trigger" };
    const newRules = [...conditionRules, newRule];
    setConditionRules(newRules);

    if (selectedNode) {
      setNodes((nds) => nds.map(n => {
        if (n.id === selectedNode.id) {
          return {
            ...n,
            data: { ...n.data, config: getNewConfig(conditionLogic, newRules) }
          }
        }
        return n;
      }));
    }
  };

  const removeRule = (index: number) => {
    const newRules = conditionRules.filter((_, i) => i !== index);
    setConditionRules(newRules);
    if (selectedNode) {
      setNodes((nds) => nds.map(n => {
        if (n.id === selectedNode.id) {
          return {
            ...n,
            data: { ...n.data, config: getNewConfig(conditionLogic, newRules) }
          }
        }
        return n;
      }));
    }
  };

  if (selectedEdge) {
    return (
      <>
        <AlertDialog open={showDeleteEdgeDialog} onOpenChange={setShowDeleteEdgeDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Edge</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this edge? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="w-30">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  onDeleteEdge(selectedEdge.id);
                  setShowDeleteEdgeDialog(false);
                }}
                className="bg-red-600 hover:bg-red-700 hover:text-pretty"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <div className="w-64 bg-white border-l border-gray-200 p-4 flex flex-col h-full">
          <div className="flex-grow">
            <h2 className="text-lg font-semibold text-gray-700 mb-4">
              Edge Properties
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Label
              </label>
              <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-600 text-xs font-mono break-all">
                {selectedEdge.label || "No label"}
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowDeleteEdgeDialog(true)}
            className="w-full bg-red-50 text-red-600 border border-red-200 py-2 rounded-md hover:bg-red-100 transition-colors font-medium mt-4"
          >
            Delete Edge
          </button>
        </div>
      </>
    );
  }

  if (!selectedNode) {
    return (
      <div className="w-64 bg-white border-l border-gray-200 p-4">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Workflow Settings
        </h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Workflow Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={workflowName}
            onChange={(e) => {
              setWorkflowName(e.target.value);
              if (showNameError && e.target.value.trim()) {
                setShowNameError(false);
              }
            }}
            placeholder="Enter workflow name"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${showNameError
              ? "border-red-500 focus:ring-red-500"
              : "border-gray-300 focus:ring-blue-500"
              }`}
            required
          />
          {showNameError ? (
            <p className="text-xs text-red-500 mt-1">
              Workflow name is required
            </p>
          ) : (
            !workflowName && (
              <p className="text-xs text-gray-500 mt-1">
                Required to create workflow
              </p>
            )
          )}
        </div>
        <div className="text-gray-500 text-sm mt-6">
          Select a node or edge to edit properties
        </div>
      </div>
    );
  }

  return (
    <>
      <AlertDialog open={showDeleteNodeDialog} onOpenChange={setShowDeleteNodeDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Node</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this node? This will also remove all connected edges. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="w-30">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                onDelete(selectedNode.id);
                setShowDeleteNodeDialog(false);
              }}
              className="bg-red-600 hover:bg-red-700 hover:text-pretty"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="w-64 bg-white border-l border-gray-200 flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-700">
            Node Properties
          </h2>
        </div>
        <div className="flex-grow overflow-y-auto p-4">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-600 capitalize">
              {selectedNode.type}
            </div>
          </div>

          {/* Condition Configuration Section */}
          {selectedNode.type === "condition" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Condition Rules
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logic
                </label>
                <Select
                  value={conditionLogic}
                  onValueChange={(value: "AND" | "OR") => handleConditionConfigChange("logic", value)}
                >
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Select logic" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AND">AND (All match)</SelectItem>
                    <SelectItem value="OR">OR (Any match)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {conditionRules.map((rule, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-md mb-3 border border-gray-200 relative group">
                  <button
                    onClick={() => removeRule(index)}
                    className="absolute top-1 right-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove Rule"
                  >
                    &times;
                  </button>
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Field</label>
                    {formFields.length > 0 ? (
                      <div className="space-y-1">
                        <Select
                          value={formFields.find(f => f.id === rule.field) ? rule.field : ""}
                          onValueChange={(value) => {
                            const finalValue = value === "custom" ? "" : value;
                            handleConditionConfigChange("rule", finalValue, index, "field");
                          }}
                        >
                          <SelectTrigger className="w-full text-xs">
                            <SelectValue placeholder="Select custom field" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">Select custom field</SelectItem>
                            {formFields.map(field => (
                              <SelectItem key={field.id} value={field.id}>
                                {field.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!formFields.find(f => f.id === rule.field) && (
                          <div className="relative">
                            <DynamicVariableInput
                              type="input"
                              value={rule.field}
                              onChange={(val) => handleConditionConfigChange("rule", val, index, "field")}
                              placeholder="Type custom variable"
                              categories={contextAwareCategories}
                              className="text-sm"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <DynamicVariableInput
                        type="input"
                        value={rule.field}
                        onChange={(val) => handleConditionConfigChange("rule", val, index, "field")}
                        placeholder="e.g. {.field-name}"
                        categories={contextAwareCategories}
                        className="text-sm"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 mb-2">
                    <div className="w-1/2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Operator</label>
                      <Select
                        value={rule.operator}
                        onValueChange={(value) => handleConditionConfigChange("rule", value, index, "operator")}
                      >
                        <SelectTrigger className="w-full text-xs">
                          <SelectValue placeholder="Operator" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="equals">Equals</SelectItem>
                          <SelectItem value="not_equals">Not Equals</SelectItem>
                          <SelectItem value="contains">Contains</SelectItem>
                          <SelectItem value="greater_than">Greater Than</SelectItem>
                          <SelectItem value="less_than">Less Than</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-1/2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Value</label>
                      <input
                        type="text"
                        value={rule.value}
                        onChange={(e) => handleConditionConfigChange("rule", e.target.value, index, "value")}
                        placeholder="Value"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={addRule}
                className="w-full py-1 text-sm text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                + Add Rule
              </button>
            </div>
          )}

          {/* Trigger Configuration Section */}
          {selectedNode.type === "trigger" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Trigger Configuration
              </h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trigger Type
                </label>
                <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-600">
                  {(selectedNode.data.config as TriggerConfig).trigger_type}
                </div>
              </div>

              {triggerType === "schedule" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Schedule Type
                    </label>
                    <Select
                      value={scheduleConfig.schedule_type}
                      onValueChange={(value) => handleTriggerConfigChange("schedule", "schedule_type", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">Day</SelectItem>
                        <SelectItem value="week">Week</SelectItem>
                        <SelectItem value="month">Month</SelectItem>
                        <SelectItem value="hour">Hour</SelectItem>
                        <SelectItem value="minute">Minute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Interval
                    </label>
                    <input
                      type="number"
                      value={scheduleConfig.interval}
                      onChange={(e) => handleTriggerConfigChange("schedule", "interval", e.target.value)}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date Time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleConfig.start_date_time}
                      onChange={(e) => handleTriggerConfigChange("schedule", "start_date_time", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Date Time
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduleConfig.end_date_time}
                      onChange={(e) => handleTriggerConfigChange("schedule", "end_date_time", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              {triggerType === "event" && (selectedNode.data.config as TriggerConfig).event_trigger_type === "form_submission" && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Form <span className="text-red-500">*</span>
                  </label>
                  {isFormListLoading ? (
                    <div className="text-sm text-gray-500">Loading forms...</div>
                  ) : (
                    <Popover open={openFormPopover} onOpenChange={setOpenFormPopover}>
                      <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openFormPopover}
                          className="w-full justify-between"
                        >
                          {eventConfig.source_id
                            ? formList?.find((form) => form.value.toString() === eventConfig.source_id?.toString())?.label
                            : "Select form..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search form..." />
                          <CommandList>
                            <CommandEmpty>No form found.</CommandEmpty>
                            <CommandGroup>
                              {formList?.map((form) => (
                                <CommandItem
                                  key={form.value}
                                  value={form.label}
                                  onSelect={() => {
                                    handleTriggerConfigChange("event", "source_id", form.value.toString());
                                    setOpenFormPopover(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      eventConfig.source_id?.toString() === form.value.toString() ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {form.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              )}

              {triggerType === "event" && (selectedNode.data.config as TriggerConfig).event_trigger_type === "task_status_change" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Task Listing
                    </label>
                    <Popover open={openTaskPopover} onOpenChange={setOpenTaskPopover}>
                      <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openTaskPopover}
                          className="w-full justify-between overflow-hidden"
                        >
                          <span className="truncate text-left flex-1">
                            {eventConfig.task_id
                              ? taskList.find((task) => task.value.toString() === eventConfig.task_id?.toString())?.label
                              : "Select task..."}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search task..."
                            value={taskSearch}
                            onValueChange={setTaskSearch}
                          />
                          <CommandList onScroll={handleTaskScroll}>
                            <CommandEmpty>No task found.</CommandEmpty>
                            <CommandGroup>
                              {taskList.map((task) => (
                                <CommandItem
                                  key={task.value}
                                  value={task.label}
                                  onSelect={() => {
                                    handleTriggerConfigChange("event", "task_id", task.value.toString());
                                    setOpenTaskPopover(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      eventConfig.task_id?.toString() === task.value.toString() ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {task.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <div className="h-10 w-full flex items-center justify-center border-t border-transparent">
                              {isTasksFetching ? (
                                <p className="text-xs text-gray-400 italic">Fetching more tasks...</p>
                              ) : (
                                tasksTable && accumulatedTasks.length < tasksTable.filtered_count && (
                                  <p className="text-[10px] text-gray-300">Scroll for more</p>
                                )
                              )}
                            </div>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Status
                    </label>
                    <Select
                      value={eventConfig.current_status || ""}
                      onValueChange={(value) => handleTriggerConfigChange("event", "current_status", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select current status" />
                      </SelectTrigger>
                      <SelectContent>
                        {taskStatusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Status
                    </label>
                    <Select
                      value={eventConfig.new_status || ""}
                      onValueChange={(value) => handleTriggerConfigChange("event", "new_status", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                      <SelectContent>
                        {taskStatusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {triggerType === "event" && (selectedNode.data.config as TriggerConfig).event_trigger_type === "ticket_status_change" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ticket Listing
                    </label>
                    <Popover open={openTicketPopover} onOpenChange={setOpenTicketPopover}>
                      <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openTicketPopover}
                          className="w-full justify-between overflow-hidden"
                        >
                          <span className="truncate text-left flex-1">
                            {eventConfig.ticket_id
                              ? ticketListOptions.find((ticket) => ticket.value.toString() === eventConfig.ticket_id?.toString())?.label
                              : "Select ticket..."}
                          </span>
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder="Search ticket..."
                            value={ticketSearch}
                            onValueChange={setTicketSearch}
                          />
                          <CommandList onScroll={handleTicketScroll}>
                            <CommandEmpty>No ticket found.</CommandEmpty>
                            <CommandGroup>
                              {ticketListOptions.map((ticket) => (
                                <CommandItem
                                  key={ticket.value}
                                  value={ticket.label}
                                  onSelect={() => {
                                    handleTriggerConfigChange("event", "ticket_id", ticket.value.toString());
                                    setOpenTicketPopover(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      eventConfig.ticket_id?.toString() === ticket.value.toString() ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {ticket.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                            <div className="h-10 w-full flex items-center justify-center border-t border-transparent">
                              {isTicketsFetching ? (
                                <p className="text-xs text-gray-400 italic">Fetching more tickets...</p>
                              ) : (
                                ticketsTable && accumulatedTickets.length < ticketsTable.filtered_count && (
                                  <p className="text-[10px] text-gray-300">Scroll for more</p>
                                )
                              )}
                            </div>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Current Status
                    </label>
                    <Select
                      value={eventConfig.current_status || ""}
                      onValueChange={(value) => handleTriggerConfigChange("event", "current_status", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select current status" />
                      </SelectTrigger>
                      <SelectContent>
                        {ticketStatusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Status
                    </label>
                    <Select
                      value={eventConfig.new_status || ""}
                      onValueChange={(value) => handleTriggerConfigChange("event", "new_status", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select new status" />
                      </SelectTrigger>
                      <SelectContent>
                        {ticketStatusOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Action Configuration Section - Only for Action Nodes */}
          {selectedNode.type === "action" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Action Configuration
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action Type
                </label>
                <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-600">
                  {ACTION_LABELS[actionType] || actionType}
                </div>
              </div>

              {actionType === "email" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To (Email) <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col gap-2">
                      <DynamicVariableInput
                        type="input"
                        value={emailTo}
                        onChange={(val) => {
                          setEmailTo(val);
                          handleActionConfigChange("to", val);
                        }}
                        placeholder="user@example.com or {variable}"
                        categories={contextAwareCategories}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Subject
                    </label>
                    <DynamicVariableInput
                      type="input"
                      value={emailSubject}
                      onChange={(val) => {
                        setEmailSubject(val);
                        handleActionConfigChange("email_subject", val);
                      }}
                      placeholder="Enter email subject"
                      categories={contextAwareCategories}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Body
                    </label>
                    <DynamicVariableInput
                      type="textarea"
                      value={emailBody}
                      onChange={(val) => {
                        setEmailBody(val);
                        handleActionConfigChange("email_body", val);
                      }}
                      placeholder="Hi {user.name}, your request has been received..."
                      rows={4}
                      categories={contextAwareCategories}
                    />
                  </div>
                </>
              )}

              {(actionType === "whatsapp" || actionType === "sms") && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-col gap-2">
                      <DynamicVariableInput
                        type="input"
                        value={whatsappPhone}
                        onChange={(val) => {
                          setWhatsappPhone(val);
                          handleActionConfigChange("number", val);
                        }}
                        placeholder="+923123456789 or {variable}"
                        categories={contextAwareCategories}
                      />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message
                    </label>
                    <div className="flex flex-col gap-2">
                      <DynamicVariableInput
                        type="textarea"
                        value={whatsappMessage}
                        onChange={(val) => {
                          setWhatsappMessage(val);
                          handleActionConfigChange("message", val);
                        }}
                        placeholder={`Hi {user.name}, your ${actionType === "whatsapp" ? "WhatsApp" : "SMS"} message...`}
                        rows={4}
                        categories={contextAwareCategories}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Webhook Configuration in Action */}
              {actionType === "webhook" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Webhook URL
                    </label>
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => handleActionConfigChange("webhook_url", e.target.value)}
                      placeholder="https://api.example.com/webhook"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Method
                    </label>
                    <Select
                      value={webhookMethod}
                      onValueChange={(value) => handleActionConfigChange("webhook_method", value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Method" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">GET</SelectItem>
                        <SelectItem value="POST">POST</SelectItem>
                        <SelectItem value="PUT">PUT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Headers (JSON)
                    </label>
                    <textarea
                      value={webhookHeaders}
                      onChange={(e) => handleActionConfigChange("webhook_headers", e.target.value)}
                      placeholder='{"Content-Type": "application/json"}'
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>

                  {(webhookMethod === "POST" || webhookMethod === "PUT") && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        JSON Body
                      </label>
                      <textarea
                        value={webhookBody}
                        onChange={(e) => handleActionConfigChange("webhook_body", e.target.value)}
                        placeholder='{"key": "value"}'
                        rows={6}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Read Form Responses Configuration in Action */}
              {actionType === "read_form_responses" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form <span className="text-red-500">*</span>
                    </label>
                    {hasPreviousFormContext(selectedNode, nodes, edges) ? (
                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        Form will be identified from the previous workflow context.
                      </div>
                    ) : (
                      <>
                        {isFormListLoading ? (
                          <div className="text-sm text-gray-500">Loading forms...</div>
                        ) : (
                          <Select
                            value={formId?.toString() || (formList?.[0]?.value?.toString() || "")}
                            onValueChange={(value) => {
                              setFormId(value);
                              handleActionConfigChange("form_id", value);
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select form" />
                            </SelectTrigger>
                            <SelectContent>
                              {formList?.map((form) => (
                                <SelectItem key={form.value} value={form.value.toString()}>
                                  {form.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Manual form selection because no previous form context was found.</p>
                      </>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Conditions (JSON)
                    </label>
                    <textarea
                      value={formConditions}
                      onChange={(e) => {
                        setFormConditions(e.target.value);
                        handleActionConfigChange("form_conditions", e.target.value);
                      }}
                      placeholder='{"field": "value"}'
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Optional JSON conditions to filter responses</p>
                  </div>
                </>
              )}

              {/* Update Form Status Configuration */}
              {actionType === "update_form_status" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Form <span className="text-red-500">*</span>
                    </label>
                    {hasPreviousFormContext(selectedNode, nodes, edges) ? (
                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        Form will be identified from the previous workflow context.
                      </div>
                    ) : (
                      <>
                        {isFormListLoading ? (
                          <div className="text-sm text-gray-500">Loading forms...</div>
                        ) : (
                          <Select
                            value={formId?.toString() || (formList?.[0]?.value?.toString() || "")}
                            onValueChange={(value) => {
                              setFormId(value);
                              handleActionConfigChange("form_id", value);
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select form" />
                            </SelectTrigger>
                            <SelectContent>
                              {formList?.map((form) => (
                                <SelectItem key={form.value} value={form.value.toString()}>
                                  {form.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <p className="text-xs text-gray-400 mt-1">Manual form selection because no previous form context was found.</p>
                      </>
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <Select
                      value={formStatus}
                      onValueChange={(value) => {
                        setFormStatus(value);
                        handleActionConfigChange("form_status", value);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {/* Ticket/Task Creation Configuration */}
              {(actionType === "ticket_creation" || actionType === "task_creation") && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <DynamicVariableInput
                      type="input"
                      value={title}
                      onChange={(val) => {
                        setTitle(val);
                        handleActionConfigChange("title", val);
                      }}
                      placeholder="New task for {user.name}"
                      categories={contextAwareCategories}
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Assignee
                    </label>
                    <Popover open={openAssigneePopover} onOpenChange={setOpenAssigneePopover}>
                      <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openAssigneePopover}
                          className="w-full justify-between"
                        >
                          {assignedTo
                            ? staffList?.find((staff: any) => staff.value.toString() === assignedTo)?.label
                            : "Select assignee..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0">
                        <Command>
                          <CommandInput placeholder="Search staff..." />
                          <CommandList>
                            <CommandEmpty>No staff found.</CommandEmpty>
                            <CommandGroup>
                              {staffList?.map((staff: any) => (
                                <CommandItem
                                  key={staff.value}
                                  value={staff.label}
                                  onSelect={() => {
                                    handleActionConfigChange("assign_to", staff.value.toString());
                                    setOpenAssigneePopover(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      assignedTo === staff.value.toString() ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  {staff.label}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </>
              )}

              {/* Sub Workflow Configuration */}
              {actionType === "sub_workflow" && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Workflow <span className="text-red-500">*</span>
                    </label>
                    {isWorkflowListLoading ? (
                      <div className="text-sm text-gray-500">Loading workflows...</div>
                    ) : (
                      <Popover open={openWorkflowPopover} onOpenChange={setOpenWorkflowPopover}>
                        <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={openWorkflowPopover}
                            className="w-full justify-between"
                          >
                            {subWorkflowId
                              ? workflowList?.find((workflow) => workflow.id.toString() === subWorkflowId.toString())?.label
                              : "Select a workflow..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 text-gray-500" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0">
                          <Command>
                            <CommandInput placeholder="Search workflow..." />
                            <CommandList>
                              <CommandEmpty>No workflow found.</CommandEmpty>
                              <CommandGroup>
                                {workflowList?.map((workflow) => (
                                  <CommandItem
                                    key={workflow.id}
                                    value={workflow.label}
                                    onSelect={() => {
                                      handleActionConfigChange("workflow_id", workflow.id.toString());
                                      setOpenWorkflowPopover(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        subWorkflowId?.toString() === workflow.id.toString() ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {workflow.label}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* AI Configuration Section - Only for AI Nodes */}
          {selectedNode.type === "ai" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                AI Configuration
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prompt <span className="text-red-500">*</span>
                </label>
                <DynamicVariableInput
                  type="textarea"
                  value={aiConfig.prompt}
                  onChange={(val) => handleAIConfigChange("prompt", val)}
                  placeholder="Analyze the following submission: {form.data}"
                  rows={4}
                  categories={contextAwareCategories}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timeout (seconds)
                </label>
                <input
                  type="number"
                  min="1"
                  value={aiConfig.timeout_seconds}
                  onChange={(e) => handleAIConfigChange("timeout_seconds", e.target.value)}
                  placeholder="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confidence Threshold
                </label>
                <Select
                  value={aiConfig.confidence_threshold}
                  onValueChange={(value) => handleAIConfigChange("confidence_threshold", value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select threshold" />
                  </SelectTrigger>
                  <SelectContent>
                    {[0, 0.3, 0.5, 0.7, 0.8, 0.9, 1.0].map((val) => (
                      <SelectItem key={val} value={val.toString()}>
                        {Math.round(val * 100)}%
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Threshold for evaluating AI result as true/false.
                </p>
              </div>

              <div className="mb-4">
                <button
                  onClick={() => setShowSystemPrompt(!showSystemPrompt)}
                  className="flex items-center text-sm font-medium text-gray-700 mb-1 hover:text-gray-900 focus:outline-none"
                >
                  {showSystemPrompt ? (
                    <ChevronDown className="h-4 w-4 mr-1" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-1" />
                  )}
                  System Prompt (Advanced)
                </button>

                {showSystemPrompt && (
                  <DynamicVariableInput
                    type="textarea"
                    value={aiConfig.system_prompt}
                    onChange={(val) => handleAIConfigChange("system_prompt", val)}
                    placeholder="Optional system prompt to guide AI behavior"
                    rows={3}
                    categories={contextAwareCategories}
                  />
                )}
              </div>
            </div>
          )}

          {/* Human Configuration Section - Only for Human Nodes */}
          {selectedNode.type === "human" && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-md font-semibold text-gray-700 mb-4">
                Human Configuration
              </h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assignee <span className="text-red-500">*</span>
                </label>
                <Popover open={openAssigneePopover} onOpenChange={setOpenAssigneePopover}>
                  <PopoverTrigger asChild className="border border-gray-300 rounded-md text-black-500">
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openAssigneePopover}
                      className="w-full justify-between"
                    >
                      {assignedTo
                        ? staffList?.find((staff: any) => staff.value.toString() === assignedTo)?.label
                        : "Select assignee..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0">
                    <Command>
                      <CommandInput placeholder="Search staff..." />
                      <CommandList>
                        <CommandEmpty>No staff found.</CommandEmpty>
                        <CommandGroup>
                          {staffList?.map((staff: any) => (
                            <CommandItem
                              key={staff.value}
                              value={staff.label}
                              onSelect={() => {
                                handleHumanConfigChange("assign_to" as any, staff.value.toString());
                                handleHumanConfigChange("assignee_email" as any, staff.email.toString());
                                setAssignedTo(staff.value.toString());
                                setOpenAssigneePopover(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  assignedTo === staff.value.toString() ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {staff.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions
                </label>
                <DynamicVariableInput
                  type="textarea"
                  value={humanConfig.instructions}
                  onChange={(val) => handleHumanConfigChange("instructions", val)}
                  placeholder="Please review submission by {user.name}..."
                  rows={3}
                  categories={contextAwareCategories}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timeout (minutes)
                </label>
                <input
                  type="number"
                  min="1"
                  value={humanConfig.timeout_minutes}
                  onChange={(e) => handleHumanConfigChange("timeout_minutes", e.target.value)}
                  placeholder="60"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Default: 60 minutes</p>
              </div>
            </div>
          )}
        </div>

        {/* Delete Node Button - Fixed at Bottom */}
        <div className="p-4 border-t border-gray-200 flex-shrink-0">
          <button
            onClick={() => setShowDeleteNodeDialog(true)}
            className="w-full bg-red-50 text-red-600 border border-red-200 py-2 rounded-md hover:bg-red-100 transition-colors font-medium text-sm"
          >
            Delete Node
          </button>
        </div>
      </div>
    </>
  );
};
